import Controller, { OnAfterFetch, OnAfterNavigate, OnBeforeSubmit, OnChange, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { AutocompleteAPI, CheckboxAPI, DateFieldAPI, FieldAPI, FormAPI, InputAPI, LocatorAPI, RadioGroupAPI, SelectableItem, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpService, Validator } from '@philips-internal/oswald-ui-core/lib/services';
import { Value } from '@philips-internal/oswald-ui-core';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';

import { Inject } from '@odinjs/odin';

import moment, { Moment } from 'moment';

import { FragmentNames, HealthHistoryAllergiesCreateAliases } from '../../../../codegen';
import BaseController from '../../../BaseController';
import { Endpoint } from '../../../../constants/endpoints';
import { WizardSteps } from '../../../../constants/wizards';
import { AllergiesDefaultValues, AllergiesRadioGroupExpressions, AllergiesRadioGroupId } from '../radio-group-data/allergies-radio-group-expressions';
import { HealthHistoryListContext } from '../../service/health-history-list-service';
import { DomainType, HealthHistoryAllergiesResponse, HealthHistoryType, SearchType } from '../../health-history-types';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import DialogService from '../../../../services/dialog-service';
import { YesNoType } from '../../../../utils/enum-utils';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { DEFAULT_DATE_PATTERN, ISO_FULL_DATE_FORMAT, MONTH_YEAR_PATTERN, YEAR_PATTERN } from '../../../../constants/date-utils';
import { AllergiesDateHandleStartOf, AllergiesDateTypeId } from '../autocomplete-data/allergies-autocomplete-expressions';
import HealthHistoryAllergiesCommons from '../health-history-allergies-commons/health-history-allergies-commons';

@Controller({ component: HealthHistoryAllergiesCreateAliases.LAYOUT_HEALTH_HISTORY_ALLERGIES, fragment: FragmentNames.HEALTH_HISTORY_ALLERGIES_CREATE })
export default class HealthHistoryAllergiesCreateController extends BaseController {

  @Inject(HealthHistoryAllergiesCreateAliases.WIZARD)
  private readonly wizardAPI!: WizardAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.FORM_ONE)
  private readonly formOneAPI!: FormAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.FORM_TWO)
  private readonly formTwoAPI!: FormAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.FORM_THREE)
  private readonly formThreeAPI!: FormAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.RADIO_GROUP_HIGHLIGHT_INTENSITY_SEVERITY)
  private readonly radioGroupHighlightIntensitySeverityAPI!: RadioGroupAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.AUTOCOMPLETE_CAUSATIVE_AGENT)
  private readonly autoCompleteCausativeAgentAPI!: AutocompleteAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.FIELD_ACTIVE_INGREDIENT)
  private readonly fieldActiveIngredientAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.LOCATOR_ACTIVE_INGREDIENT)
  private readonly locatorActiveIngredientAPI!: LocatorAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.INPUT_ACTIVE_INGREDIENT)
  private readonly inputActiveIngredientAPI!: InputAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.FIELD_DCB)
  private readonly fieldDcbAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.LOCATOR_DCB)
  private readonly locatorDcbAPI!: LocatorAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.INPUT_DCB)
  private readonly inputDcbAPI!: InputAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.LOCATOR_MATERIAL_MEDICINE)
  private readonly locatorMaterialMedicineAPI!: LocatorAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.INPUT_MATERIAL_MEDICINE)
  private readonly inputMaterialMedicineAPI!: InputAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.LOCATOR_MATERIAL_MEDICINE_CLASS)
  private readonly locatorMaterialMedicineClassAPI!: LocatorAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.INPUT_MATERIAL_MEDICINE_CLASS)
  private readonly inputMaterialMedicineClassAPI!: InputAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.LOCATOR_FAMILY)
  private readonly locatorFamilyAPI!: LocatorAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.INPUT_FAMILY)
  private readonly inputFamilyAPI!: InputAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.AUTOCOMPLETE_REACTION_TYPE)
  private readonly autoCompleteReactionTypeAPI!: AutocompleteAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.RADIO_GROUP_CONDITION)
  private readonly radioGroupConditionAPI!: RadioGroupAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.AUTOCOMPLETE_APPROACH)
  private readonly autoCompleteApproachAPI!: AutocompleteAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.AUTOCOMPLETE_DATE_TYPE)
  private readonly autoCompleteDateTypeAPI!: AutocompleteAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.FIELD_START_DATE)
  private readonly fieldStartDateAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.DATE_FIELD_START_DATE)
  private readonly dateFieldStartDateAPI!: DateFieldAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.FIELD_START_MONTH)
  private readonly fieldStartMonthAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.INPUT_START_MONTH)
  private readonly inputStartMonthAPI!: InputAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.FIELD_START_YEAR)
  private readonly fieldStartYearAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.INPUT_START_YEAR)
  private readonly inputStartYearAPI!: InputAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.DATE_FIELD_END_DATE)
  private readonly dateFieldEndDateAPI!: DateFieldAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.DATE_FIELD_LAST_DATE)
  private readonly dateFieldLastDateAPI!: DateFieldAPI;

  @Inject(HealthHistoryAllergiesCreateAliases.INPUT_UNREGISTERED_CAUSATIVE_AGENT)
  private readonly inputUnregisteredCausativeAgentAPI!: InputAPI;

  @OnMount({ target: HealthHistoryAllergiesCreateAliases.FORM_ONE })
  handleMountFormOne(event: MountEvent): void {
    const api = event.target as FormAPI;
    api.setData({...api.getData(), patientAlerts: true});
  }

  @OnMount({ target: HealthHistoryAllergiesCreateAliases.WIZARD})
  handleMountWizard(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId, patientId });
    InformationBarUtils.collapsedBar = true;
    HealthHistoryListContext.currentTab = HealthHistoryType.ALLERGIES;
  }

  @OnMount({ target: HealthHistoryAllergiesCreateAliases.RADIO_GROUP_CLASSIFICATION })
  handleMountRadioGroupClassification(event: MountEvent): void {
    const radioGroup = event.target as RadioGroupAPI;
    this.handleRadioGroupValues(AllergiesRadioGroupId.CLASSIFICATION, radioGroup, AllergiesRadioGroupExpressions.CLASSIFICATION, AllergiesDefaultValues.CLASSIFICATION);
  }

  @OnMount({ target: HealthHistoryAllergiesCreateAliases.RADIO_GROUP_HIGHLIGHT_INTENSITY_SEVERITY })
  handleMountRadioGroupHighLightIntensitySeverity(event: MountEvent): void {
    const radioGroup = event.target as RadioGroupAPI;
    this.handleRadioGroupValues(AllergiesRadioGroupId.HIGHLIGHT_INTENSITY_SEVERITY, radioGroup, AllergiesRadioGroupExpressions.HIGHLIGHT_INTENSITY_SEVERITY, AllergiesDefaultValues.HIGHLIGHT_INTENSITY_SEVERITY);
  }

  @OnMount({ target: HealthHistoryAllergiesCreateAliases.RADIO_GROUP_CONDITION })
  handleMountRadioGroupCondition(event: MountEvent): void {
    const radioGroup = event.target as RadioGroupAPI;
    this.handleRadioGroupValues(AllergiesRadioGroupId.CONDITION, radioGroup, AllergiesRadioGroupExpressions.CONDITION, AllergiesDefaultValues.CONDITION);
  }

  @OnAfterFetch({ target: HealthHistoryAllergiesCreateAliases.FETCH_DATE_TYPE})
  handleMountAutoCompleteDateType(event: FetchEvent): void {
    const dateTypeDefaultValue = (event.data as Record<string, Value>[])[1];
    this.autoCompleteDateTypeAPI.setValue(dateTypeDefaultValue);
  }

  @OnChange(HealthHistoryAllergiesCreateAliases.CHECKBOX_DENIES_ALLERGIES)
  handleChangeCheckboxDeniesAllergies(event: ChangeEvent<CheckboxAPI>): void {
    const isDeniesAllergies = event.value as unknown as boolean;
    this.context.set({ isDeniesAllergies });
    this.handleDeniesDisabledFields();
  }

  @OnChange(HealthHistoryAllergiesCreateAliases.AUTOCOMPLETE_CAUSATIVE_AGENT)
  handleChangeAutoCompleteCausativeAgent(event: ChangeEvent<AutocompleteAPI>): void {
    const causativeAgent             = event.value as unknown as SearchType;
    const isCausativeAgentEnabled    = this.autoCompleteCausativeAgentAPI.isEnabled();
    const isMedicine                 = (causativeAgent?.medicine ?? YesNoType.YES) === YesNoType.YES;
    const isActiveIngredientRequired = (causativeAgent?.activeIngredientRequired ?? YesNoType.NO) === YesNoType.YES;
    const isDcbRequired              = (causativeAgent?.dcbRequired ?? YesNoType.NO) === YesNoType.YES;
    const isUnregistered             = (causativeAgent?.unregistered ?? YesNoType.YES) === YesNoType.YES;

    this.context.set({ isActiveIngredientRequired, isDcbRequired, isMedicine, isUnregistered });
    this.handleCausativeAgentDisabledFields(isCausativeAgentEnabled);
  }

  @OnChange(HealthHistoryAllergiesCreateAliases.LOCATOR_ACTIVE_INGREDIENT)
  handleChangeLocatorActiveIngredient(): void {
    this.handleDisabledFields(this.locatorActiveIngredientAPI.getAlias(), this.locatorActiveIngredientAPI.isEnabled(), !this.inputActiveIngredientAPI.getValue());
  }

  @OnChange(HealthHistoryAllergiesCreateAliases.LOCATOR_DCB)
  handleChangeLocatorDcb(): void {
    const isUserLocalePtBr = HealthHistoryAllergiesCommons.handleUserLocale();

    if (isUserLocalePtBr) {
      this.handleDisabledFields(this.locatorDcbAPI.getAlias(), this.locatorDcbAPI.isEnabled(), !this.inputDcbAPI.getValue());
    }
  }

  @OnChange(HealthHistoryAllergiesCreateAliases.LOCATOR_MATERIAL_MEDICINE)
  handleChangeLocatorMaterialMedicine(): void {
    this.handleDisabledFields(this.locatorMaterialMedicineAPI.getAlias(), this.locatorMaterialMedicineAPI.isEnabled(), !this.inputMaterialMedicineAPI.getValue());
  }

  @OnChange(HealthHistoryAllergiesCreateAliases.LOCATOR_MATERIAL_MEDICINE_CLASS)
  handleChangeLocatorMaterialMedicineClass(): void {
    this.handleDisabledFields(this.locatorMaterialMedicineClassAPI.getAlias(), this.locatorMaterialMedicineClassAPI.isEnabled(), !this.inputMaterialMedicineClassAPI.getValue());
  }

  @OnChange(HealthHistoryAllergiesCreateAliases.LOCATOR_FAMILY)
  handleChangeLocatorFamily(): void {
    this.handleDisabledFields(this.locatorFamilyAPI.getAlias(), this.locatorFamilyAPI.isEnabled(), !this.inputFamilyAPI.getValue());
  }

  @OnChange(HealthHistoryAllergiesCreateAliases.INPUT_UNREGISTERED_CAUSATIVE_AGENT)
  handleBlurUnregisteredCausativeAgent(event: ChangeEvent<InputAPI>): void {
    this.handleDisabledFields(this.inputUnregisteredCausativeAgentAPI.getAlias(), this.inputUnregisteredCausativeAgentAPI.isEnabled(), !event.value);
  }

  @OnChange(HealthHistoryAllergiesCreateAliases.AUTOCOMPLETE_DATE_TYPE)
  handleChangeAutoCompleteDateType(event: ChangeEvent<DomainType>): void{
    const dateType = event.value;
    const oldDateType = event?.oldValue;
    this.handleDateTypeFields(dateType, oldDateType);
  }

  @OnValidate(HealthHistoryAllergiesCreateAliases.FORM_ONE)
  handleValidateFormOne(validationFormOne: ValidateEvent): void {
    this.handleStepDone(validationFormOne.valid, WizardSteps.STEP_ONE);
  }

  @OnValidate(HealthHistoryAllergiesCreateAliases.FORM_TWO)
  handleValidateFormTwo(validationFormTwo: ValidateEvent): void {
    this.handleStepDone(validationFormTwo.valid, WizardSteps.STEP_TWO);
  }

  @OnValidate(HealthHistoryAllergiesCreateAliases.FORM_THREE)
  handleValidateFormThree(validationFormThree: ValidateEvent): void {
    this.handleStepDone(validationFormThree.valid, WizardSteps.STEP_THREE);
  }

  @OnAfterNavigate(HealthHistoryAllergiesCreateAliases.WIZARD)
  handleOnMountWizardStepTwo(): void {
    this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_TWO, true);
  }

  @OnBeforeSubmit({ target: HealthHistoryAllergiesCreateAliases.WIZARD })
  handleBeforeSubmitWizard(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const user                       = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const formDataOne                = this.formOneAPI.getRecord();
    const formDataTwo                = this.formTwoAPI.getRecord();
    const formDataThree              = this.formThreeAPI.getRecord();
    const isFieldStartDateVisible    = this.fieldStartDateAPI.isVisible();
    const isFieldStartMonthVisible   = this.fieldStartMonthAPI.isVisible();
    const isFieldStartYearVisible    = this.fieldStartYearAPI.isVisible();

    const body = {
      activeIngredient: (formDataOne.activeIngredient as SearchType)?.id,
      approach: (formDataThree.approach as DomainType)?.valueDomain,
      attendance: encounterId as number,
      causativeAgent: (formDataOne.causativeAgent as SearchType)?.id,
      classification: (formDataOne.classification as SelectableItem)?.key,
      condition: (formDataTwo.condition as SelectableItem)?.key,
      dateType: (formDataThree.dateType as DomainType)?.valueDomain,
      dcb: (formDataTwo.dcb as SearchType)?.id,
      deniesAllergies: BooleanUtils.isPositiveValue(formDataOne.deniesAllergies as boolean),
      endDate: formDataThree.endDate ? moment(String(formDataThree.endDate)).utc().format(ISO_FULL_DATE_FORMAT) : undefined,
      family: (formDataTwo.family as SearchType)?.id,
      highlightIntensitySeverity: (formDataOne.highlightIntensitySeverity as SelectableItem)?.key,
      lastDate: formDataThree.lastDate ? moment(String(formDataThree.lastDate)).utc().format(ISO_FULL_DATE_FORMAT) : undefined,
      material: (formDataTwo.material as SearchType)?.id,
      materialClass: (formDataTwo.materialClass as SearchType)?.id,
      notes: formDataThree.notes,
      origin: (formDataOne.origin as DomainType)?.valueDomain,
      patientAlerts: BooleanUtils.isPositiveValue(formDataOne.patientAlerts as boolean),
      physicalPerson: patientId,
      problemsList: BooleanUtils.isPositiveValue(formDataThree.problemsList as boolean),
      professional: user.physicalPersonId,
      reactionType: (formDataTwo.reactionType as SearchType)?.id,
      securityLevel: (formDataThree.securityLevel as SearchType)?.id,
      startDate: (isFieldStartDateVisible && formDataThree.startDate) ? moment(String(formDataThree.startDate)).utc().format(ISO_FULL_DATE_FORMAT) : undefined,
      startMonth: (isFieldStartMonthVisible && formDataThree.startMonth) ? moment(String(formDataThree.startMonth)).startOf('month').utc().format(ISO_FULL_DATE_FORMAT) : undefined,
      startYear: (isFieldStartYearVisible && formDataThree.startYear) ? HealthHistoryAllergiesCommons.getOnlyYear(formDataThree.startYear) : undefined,
      unregisteredCausativeAgent: formDataTwo.unregisteredCausativeAgent
    } as Record<string, Value>;

    this.handleCreateAllergy(body);
  }

  private handleRadioGroupValues(expressionId: number, radioGroup: RadioGroupAPI, type: string, deafultValue: AllergiesDefaultValues): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        const responseBody = response.body as SelectableItem[];

        radioGroup.setData(responseBody);
        const defaultItemSelected = responseBody.find(item => item.value === deafultValue as unknown as string);
        this.formTwoAPI.setData({ ...this.formTwoAPI.getData(), [type]:defaultItemSelected });
        this.formOneAPI.setData({ ...this.formOneAPI.getData(), [type]:defaultItemSelected });

        this.formOneAPI.validate();
        this.formTwoAPI.validate();
        this.formThreeAPI.validate();

      }).catch((error) => {
        error?.body && this.showToast(error?.body, ToastLevel.ERROR)
      });
  }

  private handleDeniesDisabledFields(): void {
    const isDeniesAllergies        = this.context.get('isDeniesAllergies') as boolean;
    const isFieldStartDateVisible  = this.fieldStartDateAPI.isVisible();
    const isFieldStartMonthVisible = this.fieldStartMonthAPI.isVisible();
    const isFieldStartYearVisible  = this.fieldStartYearAPI.isVisible();
    const isUserLocalePtBr         = HealthHistoryAllergiesCommons.handleUserLocale();

    this.radioGroupHighlightIntensitySeverityAPI.setEnabled(!isDeniesAllergies);
    this.autoCompleteCausativeAgentAPI.setEnabled(!isDeniesAllergies);
    this.locatorActiveIngredientAPI.setEnabled(!isDeniesAllergies);
    this.inputActiveIngredientAPI.setEnabled(!isDeniesAllergies);
    this.locatorMaterialMedicineAPI.setEnabled(!isDeniesAllergies);
    this.inputMaterialMedicineAPI.setEnabled(!isDeniesAllergies);
    this.locatorMaterialMedicineClassAPI.setEnabled(!isDeniesAllergies);
    this.inputMaterialMedicineClassAPI.setEnabled(!isDeniesAllergies);
    this.locatorFamilyAPI.setEnabled(!isDeniesAllergies);
    this.inputFamilyAPI.setEnabled(!isDeniesAllergies);
    this.autoCompleteReactionTypeAPI.setEnabled(!isDeniesAllergies);
    this.radioGroupConditionAPI.setEnabled(!isDeniesAllergies);
    this.autoCompleteApproachAPI.setEnabled(!isDeniesAllergies);
    this.autoCompleteDateTypeAPI.setEnabled(!isDeniesAllergies);
    this.dateFieldEndDateAPI.setEnabled(!isDeniesAllergies);
    this.dateFieldLastDateAPI.setEnabled(!isDeniesAllergies);
    this.inputUnregisteredCausativeAgentAPI.setEnabled(!isDeniesAllergies);

    if (isUserLocalePtBr) {
      this.locatorDcbAPI.setEnabled(!isDeniesAllergies);
      this.inputDcbAPI.setEnabled(!isDeniesAllergies);
    }

    if (isFieldStartDateVisible) {
      this.dateFieldStartDateAPI.setEnabled(!isDeniesAllergies);
    }

    if (isFieldStartMonthVisible) {
      this.inputStartMonthAPI.setEnabled(!isDeniesAllergies);
    }

    if (isFieldStartYearVisible) {
      this.inputStartYearAPI.setEnabled(!isDeniesAllergies);
    }

    if (isDeniesAllergies) {
      this.handleDeniesEmptyFields();
    }

    this.handleMedicineRequiredFields();
  }

  private handleDeniesEmptyFields(): void {
    const emptyData                = {} as Record<string, Value> ;
    const isFieldStartDateVisible  = this.fieldStartDateAPI.isVisible();
    const isFieldStartMonthVisible = this.fieldStartMonthAPI.isVisible();
    const isFieldStartYearVisible  = this.fieldStartYearAPI.isVisible();
    const isUserLocalePtBr         = HealthHistoryAllergiesCommons.handleUserLocale();

    this.autoCompleteCausativeAgentAPI.setValue(undefined);
    this.autoCompleteReactionTypeAPI.setValue(undefined);
    this.locatorActiveIngredientAPI.setValue(emptyData);
    this.locatorMaterialMedicineAPI.setValue(emptyData);
    this.locatorMaterialMedicineClassAPI.setValue(emptyData);
    this.locatorFamilyAPI.setValue(emptyData);
    this.radioGroupConditionAPI.setValue(this.radioGroupConditionAPI.getData()[0]);
    this.autoCompleteApproachAPI.setValue(undefined);
    this.dateFieldEndDateAPI.setValue('');
    this.dateFieldLastDateAPI.setValue('');
    this.inputUnregisteredCausativeAgentAPI.setValue('');

    if (isUserLocalePtBr) {
      this.locatorDcbAPI.setValue(emptyData);
    }

    if (isFieldStartDateVisible) {
      this.dateFieldStartDateAPI.setValue('');
    }

    if (isFieldStartMonthVisible) {
      this.inputStartMonthAPI.setValue('');
    }

    if (isFieldStartYearVisible) {
      this.inputStartYearAPI.setValue('');
    }
  }

  private handleCausativeAgentDisabledFields(isCausativeAgentEnabled: boolean): void {
    const locatorEmptyData                 = {} as Record<string, Value> ;
    const inputEmptyData                   = '';
    const isDeniesAllergies                = this.context.get('isDeniesAllergies') as boolean;
    const isMedicine                       = this.context.get('isMedicine') as boolean;
    const isUnregistered                   = this.context.get('isUnregistered') as boolean;
    const enableDeniesAllergiesMedicine    = !isDeniesAllergies && isMedicine;
    const enableUnregisteredCausativeAgent = !isDeniesAllergies && isUnregistered;
    const isUserLocalePtBr                 = HealthHistoryAllergiesCommons.handleUserLocale();

    if (isCausativeAgentEnabled) {
      this.locatorActiveIngredientAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.inputActiveIngredientAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.locatorMaterialMedicineAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.inputMaterialMedicineAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.locatorMaterialMedicineClassAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.inputMaterialMedicineClassAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.locatorFamilyAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.inputFamilyAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.inputUnregisteredCausativeAgentAPI.setEnabled(enableUnregisteredCausativeAgent);

      this.locatorActiveIngredientAPI.setValue(locatorEmptyData);
      this.locatorMaterialMedicineAPI.setValue(locatorEmptyData);
      this.locatorMaterialMedicineClassAPI.setValue(locatorEmptyData);
      this.locatorFamilyAPI.setValue(locatorEmptyData);
      this.inputUnregisteredCausativeAgentAPI.setValue(inputEmptyData);

      if (isUserLocalePtBr) {
        this.locatorDcbAPI.setEnabled(enableDeniesAllergiesMedicine);
        this.inputDcbAPI.setEnabled(enableDeniesAllergiesMedicine);
        this.locatorDcbAPI.setValue(locatorEmptyData);
      }

      this.handleMedicineRequiredFields();
    }
  }

  private handleDisabledFields(componentAlias: string, isEnabled: boolean, isValid: boolean): void {

    if (!isEnabled) {
      return;
    }

    const isMedicine         = this.context.get('isMedicine') as boolean;
    const isUnregistered     = this.context.get('isUnregistered') as boolean;
    const isMatch            = (alias: string): boolean => componentAlias === alias || (isValid && isMedicine);
    const enableUnregistered = componentAlias === this.inputUnregisteredCausativeAgentAPI.getAlias() || (isValid && isUnregistered);

    this.locatorActiveIngredientAPI.setEnabled(isMatch(this.locatorActiveIngredientAPI.getAlias()));
    this.inputActiveIngredientAPI.setEnabled(isMatch(this.locatorActiveIngredientAPI.getAlias()));
    this.locatorMaterialMedicineAPI.setEnabled(isMatch(this.locatorMaterialMedicineAPI.getAlias()));
    this.inputMaterialMedicineAPI.setEnabled(isMatch(this.locatorMaterialMedicineAPI.getAlias()));
    this.locatorMaterialMedicineClassAPI.setEnabled(isMatch(this.locatorMaterialMedicineClassAPI.getAlias()));
    this.inputMaterialMedicineClassAPI.setEnabled(isMatch(this.locatorMaterialMedicineClassAPI.getAlias()));
    this.locatorFamilyAPI.setEnabled(isMatch(this.locatorFamilyAPI.getAlias()));
    this.inputFamilyAPI.setEnabled(isMatch(this.locatorFamilyAPI.getAlias()));
    this.inputUnregisteredCausativeAgentAPI.setEnabled(enableUnregistered);

    if (HealthHistoryAllergiesCommons.handleUserLocale()) {
      this.locatorDcbAPI.setEnabled(isMatch(this.locatorDcbAPI.getAlias()));
      this.inputDcbAPI.setEnabled(isMatch(this.locatorDcbAPI.getAlias()));
    }

    this.handleMedicineRequiredFields();
  }

  private handleMedicineRequiredFields(): void {
    const isActiveIngredientRequired  = this.context.get('isActiveIngredientRequired') as boolean;
    const isActiveIngredientEnabled   = this.locatorActiveIngredientAPI.isEnabled();
    const validator: Validator        = { name: 'required', options: {}};
    const isUserLocalePtBr            = HealthHistoryAllergiesCommons.handleUserLocale();

    this.fieldActiveIngredientAPI.removeValidator('required');

    if (isActiveIngredientRequired && isActiveIngredientEnabled) {
      this.fieldActiveIngredientAPI.addValidator(validator);
    }

    if (isUserLocalePtBr) {
      const isDcbRequired = this.context.get('isDcbRequired') as boolean;
      const isDcbEnabled  = this.locatorDcbAPI.isEnabled();

      this.fieldDcbAPI.removeValidator('required');

      if  (isDcbRequired && isDcbEnabled) {
        this.fieldDcbAPI.addValidator(validator);
      }
    }
  }

  private handleDateTypeFields(dateType: DomainType, oldDateType: DomainType | undefined): void {
    const oldValue = oldDateType ? this.getValueByDateType(oldDateType) : '';

    if (!dateType) {
      return
    }

    const isDayMonthYear = dateType.valueDomain === AllergiesDateTypeId.DAY_MONTH_YEAR;
    const isMonthYear    = dateType.valueDomain === AllergiesDateTypeId.MONTH_YEAR;
    const isYear         = dateType.valueDomain === AllergiesDateTypeId.YEAR;

    this.fieldStartDateAPI.setVisible(isDayMonthYear);
    this.fieldStartMonthAPI.setVisible(isMonthYear);
    this.fieldStartYearAPI.setVisible(isYear);

    if (oldValue) {
      this.setOldValueInput(oldValue, dateType);
    }
  }

  private getValueByDateType(dateType: DomainType): Moment | undefined {
    let dateValue;
    switch(dateType.valueDomain) {
      case AllergiesDateTypeId.YEAR: {
        if (!this.inputStartYearAPI.getValue()) break;
        dateValue = moment(this.inputStartYearAPI.getValue());
        break;
      }
      case AllergiesDateTypeId.MONTH_YEAR: {
        if (!this.inputStartMonthAPI.getValue()) break;
        dateValue = moment(this.inputStartMonthAPI.getValue() as string);
        break;
      }
      default: {
        if (!this.dateFieldStartDateAPI.getValue()) break;
        dateValue = moment(this.dateFieldStartDateAPI.getValue() as string);
        break;
      }
    }

    return dateValue;
  }

  private setOldValueInput(oldValue : Moment, dateType: DomainType): void  {
    switch(dateType.valueDomain) {
      case AllergiesDateTypeId.YEAR: {
        this.fieldStartYearAPI.setValue(moment(oldValue).startOf(AllergiesDateHandleStartOf.START_YEAR).format(YEAR_PATTERN));
        break;
      }
      case AllergiesDateTypeId.MONTH_YEAR: {
        this.fieldStartMonthAPI.setValue(moment(oldValue).startOf(AllergiesDateHandleStartOf.START_MONTH).format(MONTH_YEAR_PATTERN));
        break;
      }
      default: {
        this.fieldStartDateAPI.setValue(moment(oldValue).format(DEFAULT_DATE_PATTERN));
        break;
      }
    }
  }

  private handleStepDone(isStepDone: boolean, step: WizardSteps): void {
    if (isStepDone) {
      this.wizardAPI.setStepDone(step, true);
    }
  }

  private handleCreateAllergy(body: Record<string, Value>): void {
    this.formOneAPI.setLoading(true);
    HttpService.post(Endpoint.POST_HEALTH_HISTORY_ALLERGIES, undefined, { body })
      .then(response => {
        const { idHealthHistory } = response.body as HealthHistoryAllergiesResponse;
        this.showModalSubmit(idHealthHistory as number);
      })
      .catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => this.formOneAPI.setLoading(false));
  }

  private handleGoBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  private handleReleaseAllergy(idHealthHistory: number): void {
    this.formOneAPI.setLoading(true);
    HttpService.put(Endpoint.RELEASE_HEALTH_HISTORY_ALLERGIES, { sequenceNumberAllergy: idHealthHistory })
      .then(() => {
        this.formOneAPI.setLoading(false);
        applicationContext.popNavigationItem();
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
        this.formOneAPI.setLoading(false)
      });
  }

  private showModalSubmit(idHealthHistory: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.handleGoBack() },
      onClickOk: () => { this.handleReleaseAllergy(idHealthHistory) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'health-history-allergies-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}
